import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import { CreateRailTrackRequest, RailTrackResponse, UpdateRailTrackRequest } from '@yard/app/api'
import { EventTypes } from '@yard/messages/eventsTypes'
import { DeleteResponse } from '@yard/messages/models/delete-response.model'
import { railTrackService } from '@yard/services/rail-track.service'
import { computed, makeObservable } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export class RailTrackStore extends BaseEntityStore<RailTrackResponse> {
  private isFetched = false
  private readonly disposers: Array<() => void> = []

  constructor(private readonly messageBus: MessageBus) {
    super()
    makeObservable(this, {
      railTracks: computed,
    })

    this.disposers.push(
      this.messageBus.subscribeEvent<RailTrackResponse>(
        EventTypes.RailTrackUpsertedEvent,
        this.handleUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.RailTrackDeletedEvent,
        this.handleDeletedEvent,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
  }

  async loadRailTracks() {
    if (this.isFetched) return
    try {
      const railTracks = await railTrackService.list()
      this.updateStoreItems(railTracks)
      this.isFetched = true
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async addRailTrack(railTrack: CreateRailTrackRequest) {
    await railTrackService.create(railTrack)
  }

  async updateRailTrack(railTrack: UpdateRailTrackRequest) {
    await railTrackService.update(railTrack)
  }

  async deleteRailTrack(id: string) {
    await railTrackService.delete(id)
  }

  get railTracks(): Record<string, RailTrackResponse> {
    return this.items.reduce(
      (acc, track) => {
        acc[track.id] = track
        return acc
      },
      {} as Record<string, RailTrackResponse>,
    )
  }

  private handleUpsertedEvent = (event: IEvent<RailTrackResponse>) => {
    const response = event.payload
    if (!response) return

    this.updateStoreItem(response, response.id)
  }

  private handleDeletedEvent = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    this.deleteStoreItem(response.id)
  }
}
